import React, { useState, useEffect, useCallback } from "react";
import { useMeeting } from "../App";
import { 
  StickyNote, 
  X, 
  Save, 
  HelpCircle, 
  CheckSquare, 
  Plus,
  Trash2,
  MessageSquare,
  Check,
  ArrowRight,
  ExternalLink
} from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { toast } from "sonner";

const NotesPanel = () => {
  const { 
    currentMeeting, 
    updateNotes,
    questions,
    actionItems,
    addQuestion,
    updateQuestion,
    deleteQuestion,
    addActionItem,
    updateActionItem,
    deleteActionItem
  } = useMeeting();
  
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("notes"); // notes, questions, actions
  const [notes, setNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Question form
  const [newQuestion, setNewQuestion] = useState("");
  const [newQuestionPriority, setNewQuestionPriority] = useState("medium");
  
  // Action item form
  const [newAction, setNewAction] = useState("");
  const [newActionAssignee, setNewActionAssignee] = useState("");

  useEffect(() => {
    if (currentMeeting) {
      setNotes(currentMeeting.notes || "");
      // Store meeting ID for pop-out window
      localStorage.setItem('currentMeetingId', currentMeeting.id);
    }
  }, [currentMeeting]);

  // Pop-out to separate window
  const handlePopOut = () => {
    const width = 450;
    const height = 700;
    const left = window.screen.width - width - 50;
    const top = 50;
    
    window.open(
      `/session-tracker?meetingId=${currentMeeting.id}`,
      'SessionTracker',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
    setIsOpen(false);
    toast.success("Session Tracker opened in new window");
  };

  // Auto-save debounce for notes
  const debouncedSave = useCallback(
    (() => {
      let timeoutId;
      return (notesText) => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(async () => {
          try {
            setIsSaving(true);
            await updateNotes(notesText);
            setHasChanges(false);
            toast.success("Notes saved", { duration: 1500 });
          } catch (error) {
            toast.error("Failed to save notes");
          } finally {
            setIsSaving(false);
          }
        }, 1500);
      };
    })(),
    [updateNotes]
  );

  const handleNotesChange = (e) => {
    const newNotes = e.target.value;
    setNotes(newNotes);
    setHasChanges(true);
    debouncedSave(newNotes);
  };

  const handleManualSave = async () => {
    try {
      setIsSaving(true);
      await updateNotes(notes);
      setHasChanges(false);
      toast.success("Notes saved");
    } catch (error) {
      toast.error("Failed to save notes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddQuestion = async () => {
    if (!newQuestion.trim()) return;
    try {
      await addQuestion(newQuestion, newQuestionPriority);
      setNewQuestion("");
      setNewQuestionPriority("medium");
      toast.success("Question added");
    } catch (error) {
      toast.error("Failed to add question");
    }
  };

  const handleAddAction = async () => {
    if (!newAction.trim()) return;
    try {
      await addActionItem(newAction, newActionAssignee);
      setNewAction("");
      setNewActionAssignee("");
      toast.success("Action item added");
    } catch (error) {
      toast.error("Failed to add action item");
    }
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      high: "bg-red-100 text-red-700 border-red-200",
      medium: "bg-amber-100 text-amber-700 border-amber-200",
      low: "bg-slate-100 text-slate-600 border-slate-200"
    };
    return <Badge className={`text-xs ${styles[priority]}`}>{priority}</Badge>;
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: "bg-blue-100 text-blue-700 border-blue-200",
      answered: "bg-green-100 text-green-700 border-green-200",
      follow_up: "bg-amber-100 text-amber-700 border-amber-200",
      pending: "bg-slate-100 text-slate-600 border-slate-200",
      in_progress: "bg-blue-100 text-blue-700 border-blue-200",
      completed: "bg-green-100 text-green-700 border-green-200"
    };
    return <Badge className={`text-xs ${styles[status]}`}>{status.replace("_", " ")}</Badge>;
  };

  // Count badges
  const openQuestionsCount = questions?.filter(q => q.status === "open").length || 0;
  const pendingActionsCount = actionItems?.filter(a => a.status !== "completed").length || 0;

  if (!currentMeeting) return null;

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="floating-notes-btn"
        className="floating-notes-btn"
        title="Meeting Notes & Questions"
      >
        <StickyNote className="w-6 h-6" strokeWidth={1.5} />
        {(hasChanges || openQuestionsCount > 0 || pendingActionsCount > 0) && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full" />
        )}
      </button>

      {/* Notes Panel */}
      {isOpen && (
        <div className="notes-panel animate-slide-up" data-testid="notes-panel" style={{ width: "400px", maxHeight: "80vh" }}>
          <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center gap-2">
              <StickyNote className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
              <h3 className="font-semibold text-heading text-slate-900">Session Tracker</h3>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handlePopOut}
                className="p-1.5 hover:bg-blue-100 rounded-md transition-colors text-blue-600"
                title="Open in separate window (for presentations)"
                data-testid="popout-btn"
              >
                <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-slate-200 rounded-md transition-colors"
                data-testid="close-notes-btn"
              >
                <X className="w-5 h-5 text-slate-500" strokeWidth={1.5} />
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex border-b border-slate-200">
            <button
              onClick={() => setActiveTab("notes")}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === "notes" 
                  ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50/50" 
                  : "text-slate-500 hover:text-slate-700"
              }`}
              data-testid="notes-tab"
            >
              <MessageSquare className="w-4 h-4 inline mr-1" />
              Notes
            </button>
            <button
              onClick={() => setActiveTab("questions")}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors relative ${
                activeTab === "questions" 
                  ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50/50" 
                  : "text-slate-500 hover:text-slate-700"
              }`}
              data-testid="questions-tab"
            >
              <HelpCircle className="w-4 h-4 inline mr-1" />
              Questions
              {openQuestionsCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                  {openQuestionsCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("actions")}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === "actions" 
                  ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50/50" 
                  : "text-slate-500 hover:text-slate-700"
              }`}
              data-testid="actions-tab"
            >
              <CheckSquare className="w-4 h-4 inline mr-1" />
              Actions
              {pendingActionsCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-amber-500 text-white rounded-full">
                  {pendingActionsCount}
                </span>
              )}
            </button>
          </div>
          
          <div className="p-4 overflow-y-auto" style={{ maxHeight: "calc(80vh - 140px)" }}>
            {/* Session Info */}
            <div className="mb-3 pb-3 border-b border-slate-100">
              <p className="text-xs text-slate-500">
                Session: <span className="font-medium text-slate-700">{currentMeeting.customer_name}</span>
                <span className="mx-2">•</span>
                <span className="text-slate-600">{currentMeeting.company}</span>
              </p>
            </div>

            {/* Notes Tab */}
            {activeTab === "notes" && (
              <div>
                <Textarea
                  value={notes}
                  onChange={handleNotesChange}
                  placeholder="Type customer observations, comments, or general notes here..."
                  className="min-h-[200px] resize-none text-sm"
                  data-testid="notes-textarea"
                />
                
                <div className="flex items-center justify-between mt-3">
                  <p className="text-xs text-slate-400">
                    {isSaving ? "Saving..." : hasChanges ? "Unsaved changes" : "All changes saved"}
                  </p>
                  <Button
                    onClick={handleManualSave}
                    disabled={isSaving || !hasChanges}
                    size="sm"
                    className="gap-1"
                    data-testid="save-notes-btn"
                  >
                    <Save className="w-4 h-4" strokeWidth={1.5} />
                    Save
                  </Button>
                </div>
              </div>
            )}

            {/* Questions Tab */}
            {activeTab === "questions" && (
              <div className="space-y-4">
                {/* Add Question Form */}
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <Input
                    value={newQuestion}
                    onChange={(e) => setNewQuestion(e.target.value)}
                    placeholder="Enter customer question..."
                    className="mb-2 text-sm"
                    data-testid="new-question-input"
                    onKeyDown={(e) => e.key === "Enter" && handleAddQuestion()}
                  />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">Priority:</span>
                      {["high", "medium", "low"].map((p) => (
                        <button
                          key={p}
                          onClick={() => setNewQuestionPriority(p)}
                          className={`text-xs px-2 py-1 rounded ${
                            newQuestionPriority === p 
                              ? p === "high" ? "bg-red-100 text-red-700" 
                                : p === "medium" ? "bg-amber-100 text-amber-700" 
                                : "bg-slate-100 text-slate-600"
                              : "bg-white text-slate-400 border"
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                    <Button size="sm" onClick={handleAddQuestion} disabled={!newQuestion.trim()} data-testid="add-question-btn">
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                </div>

                {/* Questions List */}
                <div className="space-y-2">
                  {questions?.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-4">No questions recorded yet</p>
                  )}
                  {questions?.map((q) => (
                    <div key={q.id} className="p-3 bg-white border border-slate-200 rounded-lg" data-testid={`question-${q.id}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <p className="text-sm text-slate-800 flex-1">{q.question}</p>
                        <div className="flex items-center gap-1">
                          {getPriorityBadge(q.priority)}
                          {getStatusBadge(q.status)}
                        </div>
                      </div>
                      
                      {/* Answer Input */}
                      <Textarea
                        value={q.answer || ""}
                        onChange={(e) => updateQuestion(q.id, { answer: e.target.value })}
                        placeholder="Enter answer..."
                        className="text-sm min-h-[60px] mb-2"
                        data-testid={`question-answer-${q.id}`}
                      />
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => updateQuestion(q.id, { status: "open" })}
                            className={`text-xs px-2 py-1 rounded ${q.status === "open" ? "bg-blue-100 text-blue-700" : "bg-slate-50 text-slate-500"}`}
                          >
                            Open
                          </button>
                          <button
                            onClick={() => updateQuestion(q.id, { status: "answered" })}
                            className={`text-xs px-2 py-1 rounded ${q.status === "answered" ? "bg-green-100 text-green-700" : "bg-slate-50 text-slate-500"}`}
                          >
                            Answered
                          </button>
                          <button
                            onClick={() => updateQuestion(q.id, { status: "follow_up" })}
                            className={`text-xs px-2 py-1 rounded ${q.status === "follow_up" ? "bg-amber-100 text-amber-700" : "bg-slate-50 text-slate-500"}`}
                          >
                            Follow-up
                          </button>
                        </div>
                        <button
                          onClick={() => deleteQuestion(q.id)}
                          className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions Tab */}
            {activeTab === "actions" && (
              <div className="space-y-4">
                {/* Add Action Form */}
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <Input
                    value={newAction}
                    onChange={(e) => setNewAction(e.target.value)}
                    placeholder="Enter action item..."
                    className="mb-2 text-sm"
                    data-testid="new-action-input"
                  />
                  <div className="flex items-center gap-2">
                    <Input
                      value={newActionAssignee}
                      onChange={(e) => setNewActionAssignee(e.target.value)}
                      placeholder="Assigned to (optional)"
                      className="flex-1 text-sm"
                    />
                    <Button size="sm" onClick={handleAddAction} disabled={!newAction.trim()} data-testid="add-action-btn">
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                </div>

                {/* Actions List */}
                <div className="space-y-2">
                  {actionItems?.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-4">No action items yet</p>
                  )}
                  {actionItems?.map((a) => (
                    <div 
                      key={a.id} 
                      className={`p-3 border rounded-lg ${
                        a.status === "completed" ? "bg-green-50 border-green-200" : "bg-white border-slate-200"
                      }`}
                      data-testid={`action-${a.id}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <p className={`text-sm flex-1 ${a.status === "completed" ? "text-slate-500 line-through" : "text-slate-800"}`}>
                          {a.description}
                        </p>
                        {getStatusBadge(a.status)}
                      </div>
                      
                      {a.assigned_to && (
                        <p className="text-xs text-slate-500 mb-2">
                          <ArrowRight className="w-3 h-3 inline mr-1" />
                          Assigned to: {a.assigned_to}
                        </p>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => updateActionItem(a.id, { status: "pending" })}
                            className={`text-xs px-2 py-1 rounded ${a.status === "pending" ? "bg-slate-200 text-slate-700" : "bg-slate-50 text-slate-500"}`}
                          >
                            Pending
                          </button>
                          <button
                            onClick={() => updateActionItem(a.id, { status: "in_progress" })}
                            className={`text-xs px-2 py-1 rounded ${a.status === "in_progress" ? "bg-blue-100 text-blue-700" : "bg-slate-50 text-slate-500"}`}
                          >
                            In Progress
                          </button>
                          <button
                            onClick={() => updateActionItem(a.id, { status: "completed" })}
                            className={`text-xs px-2 py-1 rounded ${a.status === "completed" ? "bg-green-100 text-green-700" : "bg-slate-50 text-slate-500"}`}
                          >
                            <Check className="w-3 h-3 inline mr-1" />Done
                          </button>
                        </div>
                        <button
                          onClick={() => deleteActionItem(a.id)}
                          className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default NotesPanel;
